import React, { useEffect, useState } from "react";
import "./styles.css";
import { Button, Container, Table } from "react-bootstrap";
import { getTrainingRecordAPI, getTrainingRecordByPeriodAPI } from "../../apiRequests/training-management/getTrainingRecordsAPI";
import { BLANK_STRING, EXPORT_REPORT_BUTTON, EXPORT_REPORT_BUTTON_TEXT, VIEW_MODES } from "../../constants/appConstants";
import SwitchViewData from "../../Components/SwitchViewData/SwitchViewData";
import MultiSelector from "../../Components/MultiSelector/MultiSelector";
import { useNavigate, useParams } from "react-router-dom";
import { exportQuarterAndYear, findCurrentQuarter, getDifferenceDays, getQuarterString, getQuarterYearObject } from "../../utils/commonUtils";
import { updateTRCDocumentStatusAPI } from "../../apiRequests/training-management/updateTRCDocumentStatusAPI";
import { deleteTRCDocumentAPI } from "../../apiRequests/training-management/deleteTRCDocumentAPI";
import MaskLoading from "../../Components/LoadingPage/MaskLoading";
import { exportReportForTRCAPI } from "../../apiRequests/training-management/exportReportTRCAPI";
import { toast } from "react-toastify";

interface TrainingManagementProps {
    isDashboard?: boolean;
    dataTRCs?: any;
}

const TrainingManagement = (props: TrainingManagementProps) => {
    const navigate = useNavigate();
    const { period } = useParams();
    const [dataTRCs, setDataTRCs] = useState<any>([]);
    const [currentViewMode, setCurrentViewMode] = useState<string>(VIEW_MODES.DETAILED);
    const [selectedYear, setSelectedYear] = useState<string>(BLANK_STRING);
    const [isRenderSelectQuarter, setIsRenderSelectQuarter] = useState<boolean>(false);
    const [selectedQuarter, setSelectedQuarter] = useState<string>(BLANK_STRING);
    const [selectedYearAndQuarter, setSelectedYearAndQuarter] = useState<string[]>([]);
    const [dataKPIForMultiQuarter, setDataKPIForMultiQuarter] = useState<any[]>([]);
    const [isUpdateDocumentStatus, setIsUpdateDocumentStatus] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    useEffect(() => {
        !props.isDashboard && getTRCDataAndSetToState();
    }, []);
    useEffect(() => {
        const getAllTRCsData = async () => {
            const isAllDataLoaded = dataTRCs.every((data: any) => Object.values(data.quarterTRCs).every((quarterData: any) => quarterData.isLoaded));
            if (currentViewMode === VIEW_MODES.TABLE && !isAllDataLoaded) {
                setIsLoading(true);
                try {
                    const result = await getTrainingRecordAPI();
                    setDataTRCs(result);
                } catch (error) {
                    console.error("Error fetching Purchase Acceptance data:", error);
                }
                setIsLoading(false);
            }
        };
        getAllTRCsData();
    }, [currentViewMode]);
    useEffect(() => {
        if (props.isDashboard) {
            setDataTRCs(props.dataTRCs);
            setCurrentYearAndQuarter(props.dataTRCs);
        }
    }, [props.dataTRCs, props.isDashboard]);

    const setCurrentYearAndQuarter = (dataTRCs: any) => {
        const { year, quarter } = exportQuarterAndYear(period || BLANK_STRING);
        if (year && quarter) {
            const currentTRC = dataTRCs.find((data: any) => data.yearTRCs.title.includes(year));
            setSelectedYear(currentTRC?.yearTRCs?.id || BLANK_STRING);
            const currentQuarter = findCurrentQuarter(currentTRC?.quarterTRCs, year, quarter);

            setSelectedQuarter(currentQuarter || BLANK_STRING);
        } else {
            const currentYear = new Date().getFullYear();
            const currentTRC = dataTRCs.find((data: any) => data.yearTRCs.title.includes(currentYear));
            setSelectedYear(currentTRC?.yearTRCs?.id);
            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
            const selectedQuarter = findCurrentQuarter(currentTRC?.quarterTRCs, currentYear, currentQuarter);
            setSelectedQuarter(selectedQuarter || BLANK_STRING);
            const quarterString = getQuarterString(currentQuarter);
            setSelectedYearAndQuarter([`TRC Q${currentQuarter},${currentYear}`]);
            !props.isDashboard && navigate(`/training-management/${quarterString}-${currentYear}`, { replace: true });
        }
    };

    useEffect(() => {
        if (selectedYear) {
            setIsRenderSelectQuarter(true);
        }
    }, [selectedYear]);

    useEffect(() => {
        const quarterData = dataTRCs.reduce((acc: any[], item: any) => {
            const selectedQuarters = Object.keys(item.quarterTRCs)
                .filter((quarterKey: string) => selectedYearAndQuarter.includes(quarterKey))
                .map((quarterKey: string) => ({ [quarterKey]: item.quarterTRCs[quarterKey] }));

            return [...acc, ...selectedQuarters];
        }, []);
        if (quarterData) {
            const calculatedData = quarterData.map((data: any, index: number) => {
                const { KPI, passed, total } = calculateMetrics(data[selectedYearAndQuarter[index]].data);
                return { KPI, passed, total };
            });
            setDataKPIForMultiQuarter(calculatedData);
        }
    }, [selectedYearAndQuarter]);

    const getTRCDataAndSetToState = async () => {
        setIsLoading(true);
        const { year: periodYear, quarter: periodQuarter } = exportQuarterAndYear(period || BLANK_STRING);
        const targetYear = periodYear || new Date().getFullYear();
        const targetQuarter = periodQuarter || Math.floor(new Date().getMonth() / 3) + 1;
        const dataTRCs: any = await getTrainingRecordByPeriodAPI(targetYear, targetQuarter);
        setDataTRCs(dataTRCs);

        setIsUpdateDocumentStatus(false);

        if (dataTRCs.length && !isUpdateDocumentStatus) {
            setCurrentYearAndQuarter(dataTRCs);
        }
        setIsLoading(false);
    };

    const renderSelectTRCsYear = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="year">
                    Select Year:
                </label>
                <select
                    className="select"
                    id="year"
                    value={selectedYear}
                    onChange={(event) => {
                        setSelectedYear(event.target.value);
                    }}
                >
                    <option disabled value="">
                        Select Year
                    </option>
                    {dataTRCs.map((data: any) => (
                        <option key={data.yearTRCs.title} value={data.yearTRCs.id}>
                            {data.yearTRCs.title}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const handleQuarterChange = async (event: any) => {
        setIsLoading(true);
        setSelectedQuarter(event.target.value);

        const { quarter, year } = getQuarterYearObject(event.target.value);

        if (!Boolean(dataTRCs.find((data: any) => data.yearTRCs.id === selectedYear).quarterTRCs[event.target.value].isLoaded)) {
            const result: any = await getTrainingRecordByPeriodAPI(year, quarter);
            const mergedData = mergeDataTRCs(dataTRCs, result);
            setDataTRCs(mergedData);
        }

        const quarterInString = getQuarterString(quarter);
        !props.isDashboard && navigate(`/training-management/${quarterInString}-${year}`, { replace: true });
        setIsLoading(false);
    };

    function mergeDataTRCs(array1: any, array2: any) {
        const result: any = [];

        array1.forEach((item1: any, index: any) => {
            const item2 = array2[index];

            const mergedItem: any = {
                yearTRCs: item1.yearTRCs,
                quarterTRCs: {},
            };

            const allQuarters = new Set([...Object.keys(item1.quarterTRCs), ...Object.keys(item2.quarterTRCs)]);

            allQuarters.forEach((quarter) => {
                const data1 = item1.quarterTRCs[quarter]?.data || [];
                const data2 = item2.quarterTRCs[quarter]?.data || [];
                const isLoaded1 = item1.quarterTRCs[quarter]?.isLoaded || false;
                const isLoaded2 = item2.quarterTRCs[quarter]?.isLoaded || false;

                mergedItem.quarterTRCs[quarter] = {
                    data: [...data1, ...data2],
                    isLoaded: isLoaded1 || isLoaded2,
                };
            });

            result.push(mergedItem);
        });

        return result;
    }

    const renderSelectTRCsQuarter = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="quarter">
                    Select Quarter:
                </label>
                <select
                    className="select"
                    id="quarter"
                    value={selectedQuarter}
                    onChange={(event) => {
                        handleQuarterChange(event);
                    }}
                >
                    <option value="">Select Quarter</option>
                    {dataTRCs.map((data: any) => {
                        if (data.yearTRCs.id === selectedYear) {
                            return Object.keys(data.quarterTRCs).map((quarter) => (
                                <option key={quarter} value={quarter}>
                                    {quarter}
                                </option>
                            ));
                        }
                    })}
                </select>
            </div>
        );
    };

    const calculateMetrics = (dataTRCsInSelectedQuarter: any[]) => {
        const passed = dataTRCsInSelectedQuarter.reduce((acc: number, data: any) => {
            if (data.status === "PASSED") {
                return acc + 1;
            }
            return acc;
        }, 0);
        const total = dataTRCsInSelectedQuarter.length;
        const KPI = ((passed / total) * 100).toFixed(2);

        return { KPI, passed, total };
    };

    const renderTRCsDataTable = () => {
        const dataTRCsInSelectedYear = dataTRCs.find((data: any) => data.yearTRCs.id === selectedYear);
        const dataTRCsInSelectedQuarter = dataTRCsInSelectedYear.quarterTRCs[selectedQuarter].data;
        const { KPI, passed, total } = calculateMetrics(dataTRCsInSelectedQuarter);

        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{KPI}%</td>
                        <td>{passed}</td>
                        <td>{total - passed}</td>
                        <td>{total}</td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    const renderTRCsUncompleted = () => {
        const dataTRCsInSelectedYear = dataTRCs.find((data: any) => data.yearTRCs.id === selectedYear);
        const dataTRCsInSelectedQuarter = dataTRCsInSelectedYear.quarterTRCs[selectedQuarter].data;
        const uncompletedTRCs = dataTRCsInSelectedQuarter.filter((data: any) => data.status === "UNCOMPLETED");
        return (
            Boolean(uncompletedTRCs.length) && (
                <Container>
                    <p>
                        <b>Uncompleted TRCs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Subject Effective on</th>
                                <th>TRC Effective on</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {uncompletedTRCs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.effectiveOnSubject).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnTRC).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.effectiveOnSubject, data.effectiveOnTRC)}</td>
                                    <td>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                updateTRCDocumentStatus(data.id, "PASSED");
                                            }}
                                        >
                                            Mark as Passed
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };
    const getFailedTRCsData = () => {
        const dataTRCsInSelectedYear = dataTRCs.find((data: any) => data.yearTRCs.id === selectedYear);
        const dataTRCsInSelectedQuarter = dataTRCsInSelectedYear.quarterTRCs[selectedQuarter].data;
        const failedTRCs = dataTRCsInSelectedQuarter.filter((data: any) => data.status === "FAILED");
        return failedTRCs;
    };
    const renderTRCsFailed = () => {
        const failedTRCs = getFailedTRCsData();
        return (
            Boolean(failedTRCs.length) && (
                <Container>
                    <p>
                        <b>Failed TRCs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Subject Effective on</th>
                                <th>TRC Effective on</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failedTRCs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.effectiveOnSubject).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnTRC).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.effectiveOnSubject, data.effectiveOnTRC)}</td>
                                    <td>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                updateTRCDocumentStatus(data.id, "PASSED");
                                            }}
                                        >
                                            Mark as Passed
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const updateTRCDocumentStatus = async (id: string, status: string) => {
        const document = await updateTRCDocumentStatusAPI(id, status);
        const newData = dataTRCs.map((data: any) => {
            if (data.yearTRCs.id === selectedYear) {
                data.quarterTRCs[selectedQuarter].data = data.quarterTRCs[selectedQuarter].data.map((row: any) => {
                    if (row.id === id) {
                        return {
                            ...row,
                            status: document.status,
                            isStatusUpdated: true,
                        };
                    }
                    return row;
                });
            }
            return data;
        });
        setIsUpdateDocumentStatus(true);
        setDataTRCs(newData);
    };

    const removeMarkedTRCDocumentStatus = async (id: string) => {
        await deleteTRCDocumentAPI(id);
        const newData = dataTRCs.map((data: any) => {
            if (data.yearTRCs.id === selectedYear) {
                data.quarterTRCs[selectedQuarter].data = data.quarterTRCs[selectedQuarter].data.map((row: any) => {
                    if (row.id === id) {
                        let actualStatus;
                        if (row.effectiveOnSubject === BLANK_STRING || row.effectiveOnTRC === BLANK_STRING) {
                            actualStatus = "UNCOMPLETED";
                        } else {
                            actualStatus = "FAILED";
                        }

                        return {
                            ...row,
                            status: actualStatus,
                            isStatusUpdated: true,
                        };
                    }
                    return row;
                });
            }
            return data;
        });
        setIsUpdateDocumentStatus(true);
        setDataTRCs(newData);
    };
    const renderTRCsPassed = () => {
        const dataTRCsInSelectedYear = dataTRCs.find((data: any) => data.yearTRCs.id === selectedYear);
        const dataTRCsInSelectedQuarter = dataTRCsInSelectedYear.quarterTRCs[selectedQuarter].data;
        const passedTRCs = dataTRCsInSelectedQuarter.filter((data: any) => data.status === "PASSED");
        return (
            Boolean(passedTRCs.length) && (
                <Container>
                    <p>
                        <b>Passed TRCs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Subject Effective on</th>
                                <th>TRC Effective on</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {passedTRCs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.effectiveOnSubject).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnTRC).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.effectiveOnSubject, data.effectiveOnTRC)}</td>
                                    <td>
                                        {data.isStatusUpdated && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    removeMarkedTRCDocumentStatus(data.id);
                                                }}
                                            >
                                                Remove Marked
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderLoading = () => {
        return <MaskLoading></MaskLoading>;
    };
    function extractAllQuarterKeys(data: any[] = dataTRCs) {
        const allQuarterKeys: any = [];
        data.forEach((item) => {
            if (item && item.quarterTRCs) {
                const quarterKeys = Object.keys(item.quarterTRCs);
                allQuarterKeys.push(...quarterKeys);
            }
        });
        return allQuarterKeys.sort(sortStringQuarterAndYear);
    }

    const sortStringQuarterAndYear = (a: string, b: string): number => {
        const [quarterA, yearA] = a.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));
        const [quarterB, yearB] = b.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));

        if (yearA > yearB) {
            return -1;
        } else if (yearA < yearB) {
            return 1;
        } else {
            return parseInt(quarterB) - parseInt(quarterA);
        }
    };

    const renderTableMultiQuarter = () => {
        return (
            <Table>
                <thead>
                    <tr>
                        <th></th>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    {dataKPIForMultiQuarter.map((data: any, index: number) => (
                        <tr key={selectedYearAndQuarter[index]}>
                            <td>{selectedYearAndQuarter[index]}</td>
                            <td>{data.KPI}%</td>
                            <td>{data.passed}</td>
                            <td>{data.total - data.passed}</td>
                            <td>{data.total}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const renderDetailedView = () => {
        return (
            <>
                {renderSelectTRCsYear()}
                {isRenderSelectQuarter && renderSelectTRCsQuarter()}
                {renderButtonExportReport()}
                {selectedQuarter && !isLoading && renderTRCsDataTable()}
                {selectedQuarter && !isLoading && renderTRCsFailed()}
                {selectedQuarter && !isLoading && renderTRCsPassed()}
                {selectedQuarter && !isLoading && renderTRCsUncompleted()}
            </>
        );
    };

    const renderButtonExportReport = () => {
        return (
            <div className={EXPORT_REPORT_BUTTON}>
                <Button
                    variant="primary"
                    onClick={() => {
                        handleExportReport();
                    }}
                    disabled={isExporting}
                >
                    {EXPORT_REPORT_BUTTON_TEXT}
                </Button>
            </div>
        );
    };

    const handleExportReport = async () => {
        setIsExporting(true);
        const dataTRCsInSelectedYear = dataTRCs.find((data: any) => data.yearTRCs.id === selectedYear);
        const dataTRCsInSelectedQuarter = dataTRCsInSelectedYear.quarterTRCs[selectedQuarter].data;
        const { KPI, passed, total } = calculateMetrics(dataTRCsInSelectedQuarter);
        const failedTRCs = getFailedTRCsData();
        const time = selectedQuarter;
        const result = await exportReportForTRCAPI(failedTRCs, time, {
            KPI,
            passed,
            total,
        });
        setIsExporting(false);
        toast.success("Exported TRC report successfully");
    };

    const renderTableViewMultiQuarter = () => {
        return (
            <>
                <MultiSelector
                    dataOptions={extractAllQuarterKeys()}
                    currentSelected={selectedYearAndQuarter.sort(sortStringQuarterAndYear)}
                    onChange={(data: any[]) => {
                        setSelectedYearAndQuarter(data.sort(sortStringQuarterAndYear));
                    }}
                    title="Select Year And Quarter"
                ></MultiSelector>
                {selectedYearAndQuarter.length > 0 && renderTableMultiQuarter()}
            </>
        );
    };

    const renderMainContentOfTrainingManagement = () => {
        return (
            <>
                {isLoading && renderLoading()}
                <SwitchViewData currentViewMode={currentViewMode} setCurrentViewMode={(view) => setCurrentViewMode(view)}></SwitchViewData>
                {currentViewMode === VIEW_MODES.DETAILED && renderDetailedView()}
                {currentViewMode === VIEW_MODES.TABLE && renderTableViewMultiQuarter()}
            </>
        );
    };
    return <Container>{renderMainContentOfTrainingManagement()}</Container>;
};

export default TrainingManagement;
